// HelloConvo v3 — Shared Types
// Used by both frontend and backend

export type {
  NodeKind,
  PageNode,
  PageNodeData,
  VoiceDiscoveryStatus,
  VoiceDiscoveryDoc,
} from "./voice-discovery.ts";
export type {
  CrawlJob,
  CrawlJobError,
  CrawlJobStatus,
  CrawlJobType,
  CrawlPlatform,
  KnowledgeBaseEntry,
  PropertyContentCatalog,
  PropertyContentCatalogItem,
  SocialPlatform,
} from "./crawl.ts";
export { SUPPORTED_SOCIAL_PLATFORMS } from "./crawl.ts";
export type { OutputDocument, OutputGroundingSummary } from "./output-lineage.ts";
import type { OutputDocument, OutputFamily, OutputGroundingSummary, OutputMetadataSchema } from "./output-lineage.ts";

// ── Enums ──

export type Platform =
  | "facebook"
  | "linkedin"
  | "twitter"
  | "instagram"
  | "threads"
  | "tiktok"
  | "youtube"
  | "shopify"
  | "bluesky";

/** Account connections also include non-publishing analytics providers. */
export type ConnectionPlatform = Platform | "google" | "agentbeacon" | "conversion_webhook";

export type AgentName =
  | "intake"
  | "ideation"
  | "content"
  | "repurpose"
  | "distribution"
  | "learning";

/** Chat / PG message sender — pipeline agents plus synthetic system messages (e.g. daily brief). */
export type ChatMessageAgentName = AgentName | "daily_brief";

export type AgentStatus =
  | "queued"
  | "working"
  | "draft"
  | "ready"
  | "failed";

export type PublishStatus =
  | "queued"
  | "publishing"
  | "published"
  | "failed"
  | "unknown"
  | "reconciliation_required";

export type ConnectionStatus =
  | "connected"
  | "expired"
  | "error"
  | "disconnected";

export type ContentPieceStatus =
  | "draft"
  | "approved"
  | "rejected"
  | "published";

export type SubscriptionStatus =
  | "free"
  | "active"
  | "past_due"
  | "cancelled";

// ── Core Models ──

export interface User {
  id: string;
  email: string;
  name: string;
  credits: number;
  subscription_status: SubscriptionStatus;
  stripe_customer_id: string | null;
  stripe_subscription_id: string | null;
  created_at: string;
  updated_at: string;
}

/** PRD-0016 — agent-led marketing strategy (JSONB on `brands.strategy`). */
export type BrandStrategyConfidence = "draft" | "confirmed" | "needs_review";

export interface BrandStrategyPillar {
  name: string;
  angle: string;
  avoid: string;
}

export interface BrandStrategy {
  north_star: string;
  audience: {
    who: string;
    what_they_know: string;
    what_they_care_about: string;
  };
  /** Exactly three pillars — enforced at synthesis / validation. */
  pillars: [BrandStrategyPillar, BrandStrategyPillar, BrandStrategyPillar];
  success_metric: string;
  confidence: BrandStrategyConfidence;
  agent_notes: string;
  updated_at: string;
}

export interface Brand {
  id: string;
  user_id: string;
  name: string;
  logo_url: string | null;
  industry: string;
  target_audience: string;
  website_url: string | null;
  created_at: string;
  updated_at: string;
  /** Present when `brands.strategy` JSONB is set (Mech PG). */
  strategy?: BrandStrategy | null;
  canonical_artifacts?: BrandCanonicalArtifacts | null;
}

export type BrandArtifactHealthStatus = "healthy" | "missing" | "invalid";

export interface BrandCanonicalArtifactState {
  status: BrandArtifactHealthStatus;
  source: "artifact";
  manifest_version: number | null;
}

export interface BrandCanonicalArtifacts {
  strategy: BrandCanonicalArtifactState;
  voice: BrandCanonicalArtifactState;
}

export interface BrandVoice {
  id: string;
  brand_id: string;
  tone: string;
  tone_descriptors: string[];
  vocabulary: string[];
  example_posts: string[];
  guidelines: string;
  embedding: number[];
  created_at: string;
  updated_at: string;
}

/** A self-describing markdown document that tells the agent how to apply it when writing or reviewing. */
export type BrandContextDocType = "persona" | "voice_guide" | "tone" | "surface_context" | "custom";
export type BrandContextDocApplyWhen = "writing" | "reviewing" | "all";

export interface BrandContextDoc {
  document_key: string;
  brand_id: string;
  slug: string;
  doc_type: BrandContextDocType;
  name: string;
  /** When set, only loaded for requests matching this surface (e.g. "linkedin_personal"). Null = all surfaces. */
  surface: string | null;
  apply_when: BrandContextDocApplyWhen[];
  /**
   * Short imperative instruction (1–3 sentences) rendered above the doc body so the agent knows
   * exactly when and how to apply this document. When non-empty, replaces the generic metadata line.
   * Optional — legacy docs created before this field existed will have it absent at runtime.
   */
  usage_prompt?: string;
  content: string;
  enabled: boolean;
  sort_order: number;
  created_at: string;
  updated_at: string;
}

export type BrandReadinessMissingKey = "voice" | "context_docs";

export interface BrandReadiness {
  hasVoice: boolean;
  hasContextDocs: boolean;
  hasCorpus: boolean;
  isReady: boolean;
}

export const CANONICAL_ASPECT_RATIOS = [
  "1:1",
  "4:5",
  "5:4",
  "16:9",
  "9:16",
  "3:2",
  "2:3",
  "3:4",
  "4:3",
] as const;

export type CanonicalAspectRatio = (typeof CANONICAL_ASPECT_RATIOS)[number];

export interface MediaAsset {
  id: string;
  brand_id: string;
  collection_id: string | null;
  storage_id: string;
  storage_key: string;
  storage_bucket: string;
  content_hash: string;
  source_urls: string[];
  // PRD-0191: generated (mech-media), rendered (card templates), canva (P2 connector).
  primary_source: "upload" | "url_import" | "web_crawl" | "social_crawl" | "shopify" | "ugc" | "generated" | "rendered" | "canva";
  mime_type: string;
  file_size_bytes: number;
  width: number | null;
  height: number | null;
  aspect_ratio: string | null;
  shopify_image_id?: string | null;
  shopify_product_id: string | null;
  shopify_product_title: string | null;
  shopify_variant_ids: string[];
  creator_handle: string | null;
  creator_platform: string | null;
  usage_rights: "owned" | "licensed" | "ugc_approved" | "ugc_pending" | "unknown";
  label: string | null;
  user_tags: string[];
  alt_text: string | null;
  asset_type: "product_shot" | "lifestyle" | "ugc" | "ad_creative" | "brand_kit" | "social" | "other" | null;
  description: string | null;
  auto_tags: string[];
  suitable_for: string[];
  dominant_colors: string[];
  scene_type: "studio" | "outdoor" | "indoor" | "flat_lay" | "on_model" | null;
  has_person: boolean | null;
  has_text_overlay_space: boolean | null;
  processing_status: "pending" | "processing" | "complete" | "failed";
  processing_started_at?: string | null;
  processing_attempts?: number;
  caption_model: string | null;
  description_generated_at: string | null;
  processing_error: string | null;
  deleted_at: string | null;
  indexed_at: string;
  /** PRD-0191: a rendition/derivative points at its source (an OG card at its hero). */
  parent_asset_id?: string | null;
  /** PRD-0191: rendition spec name, e.g. `hero_16x9`, `og_1200x630`. */
  rendition?: string | null;
  /** PRD-0191: generator/template/canva details. Never the raw prompt: a hash plus a summary. */
  provenance?: Record<string, unknown> | null;
  /** PRD-0191: the source system's id (Canva design id + page). */
  external_ref?: string | null;
  /** List responses: signed, short-lived storage URL the browser can load directly. */
  thumbnail_url?: string | null;
  url_expires_at?: string | null;
}

export interface AssetCollection {
  id: string;
  brand_id: string;
  label: string;
  type: "shoot" | "campaign" | "shopify_import" | "ugc_batch" | "upload_batch" | "url_batch";
  asset_count: number;
  cover_asset_id: string | null;
  source_job_id: string | null;
  created_at: string;
  /** List responses: signed, short-lived URL of the cover image. */
  cover_url?: string | null;
}

export interface MediaEmbedding {
  id: string;
  brand_id: string;
  content_hash: string;
  description: string;
  generated_at: string;
}

export interface MediaAssetReference {
  asset_id: string;
  context: "html" | "plain";
  resolved?: {
    url: string;
    alt: string | null;
    width: number | null;
    height: number | null;
  };
}

// ── Agent Workbench ──

export type AgentWorkSurfaceObjectType =
  | "brand_voice"
  | "task_run"
  | "task_run_brief"
  | "daily_brief"
  | "campaign_brief"
  | "draft"
  | "research_artifact"
  | "strategy_artifact";

export type AgentWorkbenchStatus =
  | "draft"
  | "configured"
  | "in_review"
  | "ready"
  | "archived";

export type AgentWorkbenchSectionContentKind =
  | "text"
  | "markdown"
  | "string_list"
  | "labeled_fields"
  | "json";

export interface AgentWorkbenchLabeledField {
  key: string;
  label: string;
  value: string;
}

export type AgentWorkbenchSectionContent =
  | {
    kind: "text";
    text: string;
  }
  | {
    kind: "markdown";
    markdown: string;
  }
  | {
    kind: "string_list";
    items: string[];
  }
  | {
    kind: "labeled_fields";
    fields: AgentWorkbenchLabeledField[];
  }
  | {
    kind: "json";
    value: Record<string, unknown>;
  };

export interface AgentWorkbenchEvidenceRef {
  id: string;
  label: string;
  kind: string;
  href?: string | null;
}

export interface AgentWorkbenchRevisionMeta {
  updated_at: string | null;
  created_at?: string | null;
  history_count?: number | null;
  source?: string | null;
}

export interface AgentWorkbenchAction {
  id: string;
  label: string;
  description?: string | null;
  kind: "quick_action" | "custom_prompt";
  target_section_ids?: string[];
  prompt_hint?: string | null;
  enabled?: boolean;
}

export interface AgentWorkSurfaceSection {
  id: string;
  label: string;
  content_kind: AgentWorkbenchSectionContentKind;
  content: AgentWorkbenchSectionContent;
  patchable: boolean;
  summary?: string | null;
  schema?: Record<string, unknown> | null;
  evidence_refs?: AgentWorkbenchEvidenceRef[];
}

export interface AgentWorkSurfaceManifest {
  manifest_id: string;
  manifest_version: string;
  object_type: AgentWorkSurfaceObjectType;
  object_id: string;
  brand_id: string;
  title: string;
  status: AgentWorkbenchStatus;
  sections: AgentWorkSurfaceSection[];
  allowed_actions: AgentWorkbenchAction[];
  revision?: AgentWorkbenchRevisionMeta | null;
  evidence_refs?: AgentWorkbenchEvidenceRef[];
}

export type AgentWorkbenchPatchOperation =
  | {
    op: "replace_field";
    section_id: string;
    path: string;
    value: string;
  }
  | {
    op: "rewrite_text";
    section_id: string;
    path: string;
    value: string;
  }
  | {
    op: "append_list_item";
    section_id: string;
    path: string;
    value: string;
  }
  | {
    op: "remove_list_item";
    section_id: string;
    path: string;
    value: string;
  }
  | {
    op: "create_variant";
    section_id: string;
    path: string;
    label: string;
    value: AgentWorkbenchSectionContent;
  };

export interface AgentWorkbenchPatch {
  object_type: AgentWorkSurfaceObjectType;
  object_id: string;
  manifest_version: string;
  target_section_id: string | null;
  rationale: string;
  operations: AgentWorkbenchPatchOperation[];
}

export interface BrandCorpusEntry {
  id: string;
  brand_id: string;
  content: string;
  source: "upload" | "edit" | "extraction" | "task_run";
  metadata: Record<string, unknown>;
  embedding: number[];
  created_at: string;
}

export type BrandEditSourceType = "finding" | "brief" | "draft" | "freeform";
export type TrustFeedbackTag =
  | "wrong_entity"
  | "stale"
  | "off_topic"
  | "low_quality_source"
  | "useful"
  | "acted_on";

export interface DiffBrandEdit {
  kind: "diff_edit";
  id: string;
  brand_id: string;
  piece_id: string;
  ai_draft: string;
  human_version: string;
  changes: EditChange[];
  created_at: string;
  source_type: "draft";
  source_id: string | null;
  active: boolean;
  applied_at: string | null;
  applied_run_ids: string[];
  correction_text: null;
  trust_tags: [];
}

export interface CorrectionNoteBrandEdit {
  kind: "correction_note";
  id: string;
  brand_id: string;
  created_at: string;
  source_type: BrandEditSourceType;
  source_id: string | null;
  correction_text: string;
  trust_tags: TrustFeedbackTag[];
  active: boolean;
  applied_at: string | null;
  applied_run_ids: string[];
  piece_id?: string | null;
  ai_draft?: null;
  human_version?: null;
  changes?: EditChange[];
}

export type BrandEdit = DiffBrandEdit | CorrectionNoteBrandEdit;

export interface EditChange {
  type: "added" | "removed" | "changed";
  original: string;
  replacement: string;
}

// ── Platform Connections ──

/**
 * Account-owned publishing identity.
 *
 * `assignments` is the canonical brand relationship surface.
 * `brand_id` remains as a legacy shadow column during the account-scope migration
 * and must not be treated as the ownership source for new logic.
 */
export interface Connection {
  id: string;
  user_id: string;
  brand_id: string | null;
  public_id?: string | null;
  platform: ConnectionPlatform;
  /** Provider-neutral identity fields; legacy platform matching remains operational. */
  provider?: Provider | null;
  provider_subject?: string | null;
  platform_user_id: string | null;
  platform_username: string | null;
  status: ConnectionStatus;
  token_expiry: string | null;
  access_token?: string | null;
  shop?: string | null;
  /**
   * Optional safe capability summary used by the account connection list.
   * It is optional so legacy connection records and existing fixtures remain
   * valid until they opt into the additive list projection.
   */
  capabilities?: ConnectionCapabilityGrantSummary[];
  assignments: ConnectionBrandAssignment[];
  created_at: string;
  updated_at: string;
}

export interface ConnectionBrandAssignment {
  id: string;
  connection_id: string;
  brand_id: string;
  created_at: string;
  updated_at: string;
}

// ── Provider resources and cross-channel analytics (PRD-0118) ──

/** Provider identity is account-owned; brand access is modelled through assignments. */
export type Provider = "google" | "youtube" | "linkedin" | "bluesky" | "shopify" | "meta" | "instagram" | "x" | "tiktok" | "agentbeacon" | "conversion_webhook";

/** A separately consented provider feature. Unsupported providers remain explicit states. */
/**
 * Every provider capability, as one runtime list (PRD-0155 FR-4).
 *
 * The union used to be written here and then copied verbatim into three `z.enum([...])`
 * arrays across the analytics and provider-resource route files. Four places had to agree,
 * and adding a capability meant finding all of them; the type gave no help, because a
 * missing entry in a route schema is a valid schema that silently rejects the new
 * capability. Those files now build their schema from this tuple.
 */
export const PROVIDER_CAPABILITIES = [
  "agentbeacon.events.read",
  "google.search_console.read",
  "google.analytics.read",
  "google.youtube.analytics.read",
  "google.youtube.channel.read",
  "google.youtube.publish.write",
  "linkedin.analytics.read",
  "meta.analytics.read",
  "meta.facebook_page.analytics.read",
  "instagram.analytics.read",
  "x.analytics.read",
  "tiktok.analytics.read",
  /** PRD-0188: a brand's server reports signed signups/sales to HelloConvo. */
  "conversions.ingest",
] as const;

export type ProviderCapability = (typeof PROVIDER_CAPABILITIES)[number];

/** Connection-level consent includes analytics and explicit publishing grants. */
export const CONNECTION_CAPABILITIES = [
  ...PROVIDER_CAPABILITIES,
  "linkedin.publish.write",
  "bluesky.publish.write",
  "meta.facebook_page.publish.write",
] as const;
export type ConnectionCapability = (typeof CONNECTION_CAPABILITIES)[number];

/**
 * The one declaration of which capabilities the product actually offers (PRD-0155 FR-4).
 *
 * `PROVIDER_CAPABILITIES` says a capability *exists* as a name. It says nothing about
 * whether anything can serve it, so the two Google capabilities with a real adapter and the
 * six that are only names were indistinguishable, and the frontend hardcoded the difference
 * in its own cards. This is where that difference is stated once.
 *
 * `offered: true` is a promise to a user that the capability can be connected, so startup
 * refuses to run when a capability makes that promise and no adapter factory or dataset implementation can keep it —
 * see `validateProviderCatalog`.
 */
export type ProviderCatalogEntry = {
  readonly capability: ProviderCapability;
  readonly provider: Provider;
  /** Human label. The frontend renders this rather than mapping capability ids to names. */
  readonly label: string;
  readonly offered: boolean;
};

export const PROVIDER_CATALOG: readonly ProviderCatalogEntry[] = [
  { capability: "agentbeacon.events.read", provider: "agentbeacon", label: "AgentBeacon", offered: true },
  { capability: "google.search_console.read", provider: "google", label: "Search Console", offered: true },
  { capability: "google.analytics.read", provider: "google", label: "Google Analytics 4", offered: true },
  { capability: "google.youtube.analytics.read", provider: "youtube", label: "YouTube Analytics", offered: true },
  { capability: "google.youtube.channel.read", provider: "youtube", label: "YouTube Channel", offered: false },
  { capability: "google.youtube.publish.write", provider: "youtube", label: "YouTube publishing", offered: false },
  { capability: "linkedin.analytics.read", provider: "linkedin", label: "LinkedIn Analytics", offered: false },
  { capability: "meta.analytics.read", provider: "meta", label: "Meta Analytics", offered: false },
  { capability: "meta.facebook_page.analytics.read", provider: "meta", label: "Facebook Page Insights", offered: true },
  { capability: "instagram.analytics.read", provider: "instagram", label: "Instagram Professional Insights", offered: true },
  { capability: "x.analytics.read", provider: "x", label: "X Analytics", offered: false },
  { capability: "tiktok.analytics.read", provider: "tiktok", label: "TikTok Analytics", offered: false },
  // Inbound, not an analytics adapter or dataset: created through
  // /api/connections/conversion-endpoints (API-only in PRD-0188 P1), so not offered in the catalog UI yet.
  { capability: "conversions.ingest", provider: "conversion_webhook", label: "Conversion webhook", offered: false },
] as const;

export type ProviderCapabilityStatus = "available" | "connected" | "expired" | "error" | "unsupported" | "revoked";
export type CanonicalProviderResourceType =
  | "agentbeacon_service"
  | "conversion_endpoint"
  | "gsc_property"
  | "ga_account"
  | "ga4_property"
  | "ga4_data_stream"
  | "youtube_channel"
  | "social_account"
  | "facebook_page"
  | "instagram_account"
  | "shopify_store";
/** Guarded migration aliases accepted on input/read and normalized before persistence/public output. */
export type LegacyProviderResourceType = "search_console_property" | "ga4_account";
export type ProviderResourceType = CanonicalProviderResourceType | LegacyProviderResourceType;
export type CanonicalProviderResourceAssignmentRole =
  | "primary"
  | "secondary"
  | "benchmark";
/** Guarded migration aliases accepted on input/read and normalized before persistence/public output. */
export type LegacyProviderResourceAssignmentRole = "owner" | "viewer" | "analyst";
export type ProviderResourceAssignmentRole =
  | CanonicalProviderResourceAssignmentRole
  | LegacyProviderResourceAssignmentRole;
export type ProviderResourceAssignmentStatus = "active" | "ended" | "revoked";
export type ProviderResourceStatus = "available" | "partial" | "inaccessible" | "stale";
export type AnalyticsSyncKind = "discovery" | "on_demand" | "scheduled" | "backfill";
export type AnalyticsSyncStatus = "queued" | "running" | "succeeded" | "partial" | "failed" | "cancelled";
export type AnalyticsAvailability = "available" | "partial" | "unavailable" | "unsupported";
export type AnalyticsFinality = "provisional" | "final" | "corrected";

/** Non-secret account connection identity used by provider-resource rows. */
export interface ProviderConnectionIdentity {
  id: string;
  connection_id: string;
  provider: Provider;
  provider_subject: string | null;
  created_at: string;
  updated_at: string;
}

export interface ProviderCapabilityGrant {
  id: string;
  connection_id: string;
  capability: ProviderCapability;
  status: ProviderCapabilityStatus;
  granted_scopes: string[];
  granted_at: string | null;
  expires_at: string | null;
  last_error_code: string | null;
  created_at: string;
  updated_at: string;
}

/** Safe, non-secret capability fields suitable for connection list/detail DTOs. */
export type ConnectionCapabilityGrantSummary = {
  capability: ConnectionCapability;
  status: ProviderCapabilityStatus;
  granted_scopes: string[];
  expires_at: string | null;
};

/** `external_id` is intentionally opaque text: it may be a URL or a provider path, never a UUID. */
export interface ProviderResource {
  id: string;
  public_id: string;
  connection_id: string;
  provider: Provider;
  resource_type: ProviderResourceType;
  external_id: string;
  parent_resource_id: string | null;
  display_name: string | null;
  canonical_url: string | null;
  status: ProviderResourceStatus;
  availability: AnalyticsAvailability;
  metadata: Record<string, unknown>;
  first_discovered_at: string;
  last_discovered_at: string;
  created_at: string;
  updated_at: string;
}

export interface ProviderResourceCapability {
  id: string;
  provider_resource_id: string;
  capability_grant_id: string;
  permission_role: string | null;
  status: ProviderResourceStatus;
  availability: AnalyticsAvailability;
  metadata: Record<string, unknown>;
  observed_at: string;
  last_seen_at: string;
  stale_at: string | null;
  created_at: string;
  updated_at: string;
}

/** Validity intervals keep a shared provider resource from leaking history between brands. */
export interface ProviderResourceAssignment {
  id: string;
  public_id: string;
  provider_resource_id: string;
  brand_id: string;
  role: ProviderResourceAssignmentRole;
  status: ProviderResourceAssignmentStatus;
  analytics_enabled: boolean;
  valid_from: string;
  valid_until: string | null;
  backfill_start: string | null;
  backfill_end: string | null;
  explicit_collection_start?: string | null;
  explicit_collection_end?: string | null;
  explicit_collection_authorized_at?: string | null;
  explicit_collection_authorized_by?: string | null;
  surface_id: string | null;
  identity_id: string | null;
  created_at: string;
  updated_at: string;
}

export interface ProviderResourceAssignmentAudit {
  id: string;
  assignment_id: string;
  action: "created" | "updated" | "ended" | "revoked";
  actor_user_id: string | null;
  reason: string | null;
  created_at: string;
}

/** Sync failure classes are safe, non-secret, and drive retry/disable behavior. */
export type AnalyticsSyncErrorClass =
  | "retryable_provider"
  | "quota_exhausted"
  | "reconnect_required"
  | "permission_denied"
  | "unsupported_capability"
  | "invalid_request"
  | "internal";

export interface AnalyticsSyncRun {
  id: string;
  /** Canonical task-run UUID rendered as opaque text for public responses. */
  public_id: string;
  assignment_id: string;
  /** Canonical analytics attempts are always configuration-driven. */
  sync_config_id: string | null;
  capability: ProviderCapability;
  kind: AnalyticsSyncKind;
  status: AnalyticsSyncStatus;
  requested_start: string | null;
  requested_end: string | null;
  attempt: number;
  /** Durable backfill request generation captured when this run was claimed. */
  backfill_request_generation: number | null;
  row_count: number | null;
  /** Receipt for the last raw provider artifact collected by this run. */
  artifact_id: string | null;
  /** Provider cursor to resume from when a bounded run stops before exhausting pages. */
  resume_cursor: string | null;
  /** The run this attempt resumes; null for a first attempt. */
  parent_run_id: string | null;
  availability: AnalyticsAvailability | null;
  finality: AnalyticsFinality | null;
  error_class: AnalyticsSyncErrorClass | null;
  error_code: string | null;
  /** Underlying error class, own code, and credential-redacted message (failed runs). */
  error_detail?: { name: string; code: string | null; message: string } | null;
  started_at: string | null;
  completed_at: string | null;
  duration_ms: number | null;
  created_at: string;
  updated_at: string;
}

export type AnalyticsSyncConfigStatus = "active" | "disabled";
export type AnalyticsCollectionMode = "scheduled" | "explicit_only";
export type AnalyticsFreshness = "up_to_date" | "processing_lag" | "stale" | "uncollected";

/**
 * Assignment-scoped collection contract. Every routine window is clamped to the
 * assignment validity start; earlier dates require the explicit backfill window.
 */
export interface AnalyticsSyncConfig {
  id: string;
  public_id: string;
  assignment_id: string;
  capability: ProviderCapability;
  report_key: string;
  collection_mode?: AnalyticsCollectionMode;
  /** NULL/absent is legacy dispatch. Set only by an authorized source handover. */
  dispatch_schedule_id?: string | null;
  cadence_minutes: number;
  lookback_days: number;
  row_limit: number;
  enabled: boolean;
  status: AnalyticsSyncConfigStatus;
  disabled_reason: string | null;
  /** Provider pagination cursor plus the window it belongs to, for safe resume. */
  cursor: string | null;
  cursor_window_start: string | null;
  cursor_window_end: string | null;
  /** Latest date whose provider data is final; finalized windows are not re-queried. */
  finalized_through: string | null;
  next_run_at: string | null;
  last_run_at: string | null;
  last_success_at: string | null;
  last_error_class: AnalyticsSyncErrorClass | null;
  last_error_code: string | null;
  consecutive_failure_count: number;
  backfill_requested_start: string | null;
  backfill_requested_end: string | null;
  backfill_completed_through: string | null;
  /** Monotonic token preventing an older run from completing a replacement request. */
  backfill_request_generation: number;
  explicit_requested_start?: string | null;
  explicit_requested_end?: string | null;
  created_at: string;
  updated_at: string;
}

/** Durable terminal report-window coverage, including successful zero-row collection. */
export interface AnalyticsCoverageReceipt {
  id: string;
  assignment_id: string;
  sync_config_id: string;
  sync_run_id: string | null;
  report_key: string;
  requested_start: string;
  requested_end: string;
  coverage_start: string | null;
  coverage_end: string | null;
  status: AnalyticsSyncStatus;
  row_count: number;
  availability: AnalyticsAvailability | null;
  finality: AnalyticsFinality | null;
  completed_at: string | null;
  created_at: string;
}

/** Metric grain. `range` covers non-daily provider aggregates such as property totals. */
export type AnalyticsFactGrain = "day" | "range";
export type AnalyticsMetricUnit = "count" | "ratio" | "seconds" | "minutes" | "position" | "currency";
export type AnalyticsMetricAggregation =
  | "sum"
  | "weighted_ratio"
  | "weighted_average"
  | "latest"
  | "provider_defined";

/**
 * One append-safe provider observation. Sources are the audit authority: an
 * overlapping re-sync that reports the same value with the same artifact lineage
 * is idempotent, while a changed or re-finalized value is recorded as a new
 * `finality_version` instead of overwriting the earlier observation.
 */
export interface AnalyticsFactSource {
  id: string;
  assignment_id: string;
  brand_id: string;
  provider_resource_id: string;
  capability: ProviderCapability;
  report_key: string;
  /** Provider metric key, e.g. `gsc.clicks`. Projected to `AnalyticsFact.source_metric`. */
  metric_key: string;
  canonical_metric: string | null;
  period_start: string;
  period_end: string;
  grain: AnalyticsFactGrain;
  dimensions: AnalyticsDimensions;
  dimension_hash: string;
  value: number;
  unit: AnalyticsMetricUnit;
  aggregation: AnalyticsMetricAggregation;
  numerator_value: number | null;
  denominator_value: number | null;
  availability: AnalyticsAvailability;
  finality: AnalyticsFinality;
  finality_version: number;
  sync_run_id: string | null;
  artifact_id: string | null;
  surface_id: string | null;
  external_content_mapping_id: string | null;
  observed_at: string;
  created_at: string;
  updated_at: string;
}

/**
 * A provider dimension the report returned but left empty is a real, distinct
 * dimension set — it is kept as an explicit null rather than dropped, so it can
 * never hash to the same identity as a report row without that dimension.
 */
export type AnalyticsDimensions = Record<string, string | null>;

/**
 * Facts are assignment-scoped so a provider resource shared by two brands never
 * exposes one brand's history to the other. A fact is the derived canonical
 * projection of the winning `AnalyticsFactSource` for its identity.
 */
export interface AnalyticsFact {
  id: string;
  assignment_id: string;
  brand_id: string;
  provider_resource_id: string;
  capability: ProviderCapability;
  report_key: string;
  source_metric: string;
  canonical_metric: string | null;
  period_start: string;
  period_end: string;
  grain: AnalyticsFactGrain;
  dimensions: AnalyticsDimensions;
  dimension_hash: string;
  value: number;
  unit: AnalyticsMetricUnit;
  aggregation: AnalyticsMetricAggregation;
  /** Ratio/average context so aggregates recompute instead of averaging averages. */
  numerator_value: number | null;
  denominator_value: number | null;
  availability: AnalyticsAvailability;
  finality: AnalyticsFinality;
  /** Increments only when a later observation changes the value or finality. */
  finality_version: number;
  sync_run_id: string | null;
  artifact_id: string | null;
  surface_id: string | null;
  external_content_mapping_id: string | null;
  observed_at: string;
  created_at: string;
  updated_at: string;
}

export type AnalyticsRetentionClass = "standard" | "extended";

/**
 * Immutable receipt for one scrubbed provider response. The payload itself lives
 * in the internal evidence store; this row carries authorization and retention.
 */
export interface AnalyticsArtifact {
  id: string;
  public_id: string;
  brand_id: string;
  assignment_id: string;
  connection_id: string;
  provider_resource_id: string;
  sync_run_id: string | null;
  capability: ProviderCapability;
  report_key: string;
  /** OAuth actor retained as audit metadata only; it is not an access requirement. */
  actor_user_id: string | null;
  /** Minimal redacted request envelope — never headers, tokens, or credentials. */
  request: Record<string, unknown>;
  payload_ref: string;
  checksum: string;
  byte_count: number;
  provider_api_version: string | null;
  requested_start: string | null;
  requested_end: string | null;
  row_count: number | null;
  retention_class: AnalyticsRetentionClass;
  expires_at: string | null;
  payload_deleted_at: string | null;
  created_at: string;
}

export type ExternalContentLinkStatus = "linked" | "ambiguous";

export type ExternalContentMatchingMethod =
  | "publish_receipt"
  | "provider_id"
  | "canonical_url"
  | "operator";

/** Provider content ↔ HelloConvo output lineage. Ambiguous matches stay unlinked. */
export interface ExternalContentMapping {
  id: string;
  public_id: string;
  brand_id: string;
  assignment_id: string | null;
  provider: Provider;
  provider_resource_id: string | null;
  resource_type: ProviderResourceType | null;
  external_content_id: string | null;
  canonical_url: string | null;
  normalized_url: string | null;
  output_id: string | null;
  output_revision_id: string | null;
  brief_id: string | null;
  content_piece_id: string | null;
  matching_method: ExternalContentMatchingMethod;
  /**
   * `ambiguous` once two different outputs claimed the same normalized URL with
   * no provider content id to tell them apart. An ambiguous mapping is never a
   * match: the metric stays unlinked instead of being attributed to a guess.
   */
  link_status: ExternalContentLinkStatus;
  published_at: string | null;
  observed_at: string;
  created_at: string;
  updated_at: string;
}

/** Truthful per-capability data state; "no data" is never rendered as a real zero. */
export interface PublicAnalyticsHealthEntry {
  assignment_id: string;
  resource_id: string;
  /** Opaque public connection identifier used for assignment-authorized live reports. */
  connection_id: string;
  provider: Provider;
  capability: ProviderCapability;
  resource_display_name: string | null;
  connection_status: ConnectionStatus;
  capability_status: ProviderCapabilityStatus;
  resource_status: ProviderResourceStatus;
  assignment_status: ProviderResourceAssignmentStatus;
  analytics_enabled: boolean;
  availability: AnalyticsAvailability;
  sync_status: AnalyticsSyncStatus | "never_run";
  sync_enabled: boolean;
  last_success_at: string | null;
  last_error_class: AnalyticsSyncErrorClass | null;
  next_run_at: string | null;
  /** Latest fact date, not "today"; a null value means no collected period exists. */
  fresh_through: string | null;
  finality: AnalyticsFinality | null;
}

export interface PublicAnalyticsMetric {
  metric: string;
  source_metric: string | null;
  value: number | null;
  unit: AnalyticsMetricUnit;
  aggregation: AnalyticsMetricAggregation;
  availability: AnalyticsAvailability;
  finality: AnalyticsFinality | null;
  numerator_value: number | null;
  denominator_value: number | null;
}

export interface PublicAnalyticsSummary {
  brand_id: string;
  start_date: string;
  end_date: string;
  sources: Array<{
    provider: Provider;
    capability: ConnectionCapability;
    resource_id: string;
    assignment_id: string;
  }>;
  metrics: PublicAnalyticsMetric[];
  availability: AnalyticsAvailability;
  finality: AnalyticsFinality | null;
  fresh_through: string | null;
  /** Coverage-receipt based state for the requested window. */
  freshness?: AnalyticsFreshness;
  expected_through?: string | null;
}

export interface PublicAnalyticsRefreshStatus {
  schedule?: PublicSourceSchedule;
  assignment_id: string;
  connection_id: string;
  capability: ProviderCapability;
  mode: "scheduled" | "on_demand" | "unavailable";
  enabled: boolean | null;
  state: "scheduled" | "queued" | "running" | "paused" | "mixed" | "blocked" | "not_configured" | "on_demand" | "unavailable";
  can_toggle: boolean;
  blockers: string[];
  last_success_at: string | null;
  next_run_at: string | null;
  reports: PublicAnalyticsSyncStatus[];
}

export interface PublicAnalyticsSyncStatus {
  id: string;
  assignment_id: string;
  capability: ProviderCapability;
  report_key: string;
  enabled: boolean;
  status: AnalyticsSyncConfigStatus;
  disabled_reason: string | null;
  cadence_minutes: number;
  lookback_days: number;
  next_run_at: string | null;
  last_run_at: string | null;
  last_success_at: string | null;
  last_error_class: AnalyticsSyncErrorClass | null;
  consecutive_failure_count: number;
  finalized_through: string | null;
  backfill_requested_start: string | null;
  backfill_requested_end: string | null;
  backfill_completed_through: string | null;
  last_run: {
    id: string;
    kind: AnalyticsSyncKind;
    status: AnalyticsSyncStatus;
    requested_start: string | null;
    requested_end: string | null;
    attempt: number;
    row_count: number | null;
    availability: AnalyticsAvailability | null;
    finality: AnalyticsFinality | null;
    error_class: AnalyticsSyncErrorClass | null;
    started_at: string | null;
    completed_at: string | null;
    duration_ms: number | null;
  } | null;
}

/** Raw evidence read result: scrubbed payload plus its non-secret receipt. */
export interface PublicAnalyticsArtifact {
  id: string;
  capability: ProviderCapability;
  report_key: string;
  requested_start: string | null;
  requested_end: string | null;
  row_count: number | null;
  checksum: string;
  byte_count: number;
  provider_api_version: string | null;
  retention_class: AnalyticsRetentionClass;
  expires_at: string | null;
  created_at: string;
  request: Record<string, unknown>;
  /** Null once retention deleted the payload; the receipt and facts survive. */
  response: unknown;
  payload_deleted_at: string | null;
}

/** Safe public DTO: intentionally excludes access tokens, credential references, and Vault coordinates. */
export interface PublicProviderConnection {
  id: string;
  provider: Provider;
  provider_subject: string | null;
  status: ConnectionStatus;
  capabilities: ConnectionCapabilityGrantSummary[];
  assignments: ConnectionBrandAssignment[];
  created_at: string;
  updated_at: string;
}

export interface PublicProviderResource {
  id: string;
  provider: Provider;
  resource_type: CanonicalProviderResourceType;
  external_id: string;
  parent_resource_id: string | null;
  display_name: string | null;
  canonical_url: string | null;
  status: ProviderResourceStatus;
  availability: AnalyticsAvailability;
  capabilities: Array<{
    capability: ProviderCapability;
    status: ProviderResourceStatus;
    permission_role: string | null;
    last_seen_at: string;
    stale_at: string | null;
  }>;
  first_discovered_at: string;
  last_discovered_at: string;
}

export interface PublicProviderResourceAssignment {
  id: string;
  resource_id: string;
  brand_id: string;
  role: CanonicalProviderResourceAssignmentRole;
  status: ProviderResourceAssignmentStatus;
  analytics_enabled: boolean;
  valid_from: string;
  valid_until: string | null;
  backfill_start: string | null;
  backfill_end: string | null;
  explicit_collection_start?: string | null;
  explicit_collection_end?: string | null;
  explicit_collection_authorized_at?: string | null;
  surface_id: string | null;
  identity_id: string | null;
  created_at: string;
  updated_at: string;
}

export interface PublicProviderReportPage {
  rows: Array<Record<string, string | number | null>>;
  next_cursor: string | null;
  availability: AnalyticsAvailability;
  finality: AnalyticsFinality;
  quota: {
    remaining: number | null;
    reset_at: string | null;
  };
  metadata?: Record<string, unknown>;
  /** Opaque audit lineage only; raw evidence is intentionally not readable in Parent 5. */
  evidence_id: string;
}

// ── Content Pipeline ──

/** Workflow human approval gate (NoSQL `hc_workflow_human_gates`); exposed via GET /api/human-gates/pending. */
export interface HumanGate {
  id: string;
  instanceId: string;
  nodeId: string;
  briefId: string;
  brandId: string;
  title: string;
  preview: Record<string, unknown>;
  actions: string[];
  status: "pending" | "completed";
  timeoutAt: string | null;
  createdAt: string;
  resolvedAt?: string | null;
  resolvedAction?: "approve" | "reject" | "changes" | null;
  comment?: string | null;
  /** Set when a "due soon" reminder was recorded (server-side). */
  reminderSentAt?: string | null;
  /** Brief title — enriched server-side from the briefs table. */
  briefTitle?: string | null;
  /** Brief platform (e.g. "linkedin", "twitter") — enriched server-side. */
  briefPlatform?: string | null;
  /** Brief format (e.g. "text_post", "video") — enriched server-side. */
  briefFormat?: string | null;
  /** Campaign title — enriched server-side from the campaigns table. */
  campaignTitle?: string | null;
  /** Campaign goal/objective — enriched server-side. */
  campaignGoal?: string | null;
  /** Campaign-level voice overrides — enriched server-side. */
  campaignVoiceOverrides?: Record<string, unknown> | null;
  /** Brand name — enriched server-side from the brands table. */
  brandName?: string | null;
}

export interface DailyCheckin {
  id: string;
  user_id: string;
  brand_id: string;
  type: "voice" | "text";
  raw_text: string;
  transcription: string | null;
  audio_ref: string | null;
  created_at: string;
}

export interface ContentIdea {
  headline: string;
  angle: string;
  target_platforms: Platform[];
  format: string;
  estimated_engagement: string;
}

export interface ContentRun {
  id: string;
  user_id: string;
  brand_id: string;
  checkin_id: string;
  selected_idea_index: number | null;
  agent_status: Record<AgentName, AgentStatus>;
  ideas: ContentIdea[];
  checkpoint: Record<string, unknown> | null;
  created_at: string;
  updated_at: string;
}

export interface ContentPiece {
  id: string;
  run_id: string;
  brand_id: string;
  platform: Platform;
  content: string;
  ai_draft: string;
  format: string;
  character_count: number;
  hashtags: string[];
  media_suggestions: string[];
  status: ContentPieceStatus;
  platform_post_id: string | null;
  platform_post_url: string | null;
  publish_status: PublishStatus | null;
  publish_error: string | null;
  scheduled_at: string | null;
  published_at: string | null;
  created_at: string;
  updated_at: string;
}

export interface ContentPerformance {
  id: string;
  piece_id: string;
  brand_id: string;
  platform: Platform;
  metrics: EngagementMetrics;
  collected_at: string;
}

export interface EngagementMetrics {
  likes: number;
  shares: number;
  comments: number;
  clicks: number;
  impressions: number;
}

// ── Chat ──

export type ChatThreadType = "brainstorm" | "skills" | "agent" | "onboarding";

export interface ChatThread {
  id: string;
  user_id: string;
  thread_type: ChatThreadType;
  brand_id: string;
  run_id: string | null;
  idea_index: number | null;
  /** When set, agent system prompt includes campaign voice cascade (PRD-0011). */
  campaign_id: string | null;
  /** When set, agent system prompt includes brief voice cascade (PRD-0011). */
  brief_id: string | null;
  created_at: string;
  updated_at: string;
}

export type AgentThreadState = "research" | "campaign" | "brief";
export type AgentArtifactState = "unsaved" | "saved";
export type AgentResearchOutputType = "research_memo" | "brief_draft" | "artifact_bundle";

export interface AgentThreadListItem {
  id: string;
  brand_id: string;
  campaign_id: string | null;
  brief_id: string | null;
  thread_type: ChatThreadType;
  title: string;
  preview: string | null;
  state: AgentThreadState;
  /** True when a mech-run session is currently executing for this thread. */
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export interface ChatMessage {
  id: string;
  thread_id: string;
  role: "user" | "assistant";
  agent_name: ChatMessageAgentName | null;
  content: string;
  created_at: string;
  /** Persisted outcome for an assistant turn, when available. */
  metadata?: {
    agent_session_id?: string;
    agent_turn_outcome?: { status: "pending" | "completed" | "failed" | "cancelled"; code?: AgentErrorCode; retryable?: boolean };
  } | null;
}

// ── Billing ──

export interface CreditBalance {
  credits: number;
  subscription_status: SubscriptionStatus;
  low_balance: boolean;
}

export interface CreditTransaction {
  id: string;
  user_id: string;
  amount: number;
  reason: "content_run" | "subscription_renewal" | "topup" | "signup_bonus" | "agent_chat";
  run_id: string | null;
  created_at: string;
}

export type TopupPack = "10" | "30" | "100";

export interface TopupPackInfo {
  credits: number;
  price_cents: number;
  label: string;
}

// ── Projects & Research ──

export type SourceType = "website" | "social_handle" | "product_url" | "other";
export type SubjectType = "own_brand" | "competitor" | "prospect" | "other";
export type ResearchRunStatus = "pending" | "running" | "completed" | "failed";
export type ResearchSkillSlug =
  | "website_analysis"
  | "content_audit"
  | "audience_intelligence"
  | "content_opportunities";
export type DataQuality = "strong" | "partial" | "weak";

export interface Project {
  id: string;
  user_id: string;
  name: string;
  description: string | null;
  brand_id: string | null;
  created_at: string;
  updated_at: string;
}

export interface ResearchSubject {
  id: string;
  project_id: string;
  source_type: SourceType;
  domain: string | null;
  url: string | null;
  handle: string | null;
  platform: string | null;
  name: string;
  subject_type: SubjectType;
  last_researched_at: string | null;
  created_at: string;
}

export interface ResearchRun {
  id: string;
  project_id: string;
  subject_id: string;
  status: ResearchRunStatus;
  skills_requested: ResearchSkillSlug[];
  skills_completed: ResearchSkillSlug[];
  api_calls_total: number;
  api_calls_succeeded: number;
  api_calls_failed: number;
  started_at: string | null;
  completed_at: string | null;
  error: string | null;
  created_at: string;
}

export interface ResearchOutput {
  document_key: string;
  run_id: string;
  project_id: string;
  subject_id: string;
  skill_slug: ResearchSkillSlug;
  skill_version: string;
  output: Record<string, unknown>;
  api_calls_summary: { total: number; successful: number; failed: number };
  data_quality: DataQuality;
  source_task_run_id?: string | null;
  source_task_slug?: string | null;
  created_at: string;
}

// ── Conversation Monitor ──

export type MonitorPlatform =
  | "reddit"
  | "twitter"
  | "hackernews"
  | "linkedin"
  | "quora"
  | "indiehackers"
  | "producthunt";

export type ScanFrequency = "daily" | "twice_daily" | "manual_only";
export type ScanRunStatus = "pending" | "running" | "completed" | "failed";
export type OpportunityStatus = "pending" | "posted" | "skipped" | "expired";

export interface Monitor {
  id: string;
  user_id: string;
  brand_id: string;
  platforms: MonitorPlatform[];
  keywords: string[];
  competitor_names: string[];
  scan_frequency: ScanFrequency;
  auto_generate_keywords: boolean;
  is_active: boolean;
  last_scanned_at: string | null;
  created_at: string;
  updated_at: string;
}

export interface ScanRun {
  id: string;
  monitor_id: string;
  status: ScanRunStatus;
  platforms_scanned: MonitorPlatform[];
  threads_found: number;
  threads_scored: number;
  threads_above_threshold: number;
  responses_drafted: number;
  error: string | null;
  started_at: string | null;
  completed_at: string | null;
  created_at: string;
}

export interface ThreadEngagement {
  upvotes?: number;
  comments?: number;
  views?: number;
  likes?: number;
  retweets?: number;
  shares?: number;
}

export interface Opportunity {
  id: string;
  task_run_id: string;
  monitor_id: string;
  brand_id: string;
  platform: MonitorPlatform;
  thread_url: string;
  thread_title: string | null;
  thread_author: string | null;
  thread_subreddit: string | null;
  thread_engagement: ThreadEngagement;
  thread_posted_at: string | null;
  score_total: number;
  score_relevance: number;
  score_recency: number;
  score_engagement: number;
  score_openness: number;
  score_safety: number;
  draft_response: string | null;
  status: OpportunityStatus;
  skip_reason: string | null;
  posted_url: string | null;
  posted_at: string | null;
  cadence_order: number | null;
  created_at: string;
  updated_at: string;
}

export interface OpportunityThread {
  document_key: string;
  thread_url: string;
  platform: MonitorPlatform;
  title: string;
  body: string;
  author: string;
  subreddit?: string;
  comments: ThreadComment[];
  engagement: ThreadEngagement;
  posted_at: string;
  fetched_at: string;
}

export interface ThreadComment {
  author: string;
  body: string;
  score?: number;
  replies?: ThreadComment[];
}

// ── YouTube Analyzer ──

export interface YouTubeChannel {
  id: string;
  user_id: string;
  channel_url: string;
  handle: string;
  name: string;
  description: string | null;
  subscriber_count: number | null;
  video_count: number | null;
  thumbnail_url: string | null;
  indexed_video_count: number;
  last_indexed_at: string | null;
  created_at: string;
  updated_at: string;
}

export interface YouTubeVideo {
  id: string;
  channel_id: string | null;
  user_id: string;
  video_url: string;
  youtube_video_id: string;
  title: string;
  description: string | null;
  published_at: string | null;
  duration_seconds: number | null;
  view_count: number | null;
  like_count: number | null;
  comment_count: number | null;
  tags: string[] | null;
  has_transcript: boolean;
  transcript_chunk_count: number;
  created_at: string;
  updated_at: string;
}

export interface YouTubeTranscriptChunk {
  youtube_video_id: string;
  channel_id: string | null;
  user_id: string;
  video_title: string;
  chunk_index: number;
  total_chunks: number;
  text: string;
  start_offset: number;
  end_offset: number;
  created_at: string;
}

export interface YouTubeSearchResult {
  video_title: string;
  youtube_video_id: string;
  video_url: string;
  channel_id: string | null;
  chunk_text: string;
  chunk_index: number;
  total_chunks: number;
  score: number;
}

// ── Agent Chat ──

export type AgentSessionStatus = "running" | "completed" | "failed" | "cancelled";

export interface AgentSession {
  id: string;
  thread_id: string;
  user_id: string;
  brand_id: string;
  status: AgentSessionStatus;
  mech_session_id: string | null;
  summary: string | null;
  tokens_used: number | null;
  cost_usd: number | null;
  started_at: string;
  last_event_at?: string | null;
  completed_at: string | null;
}

export interface AgentToolCall {
  id: string;
  name: string;
  input: string;
  status: "running" | "success" | "failed";
  output: string | null;
  duration_ms: number | null;
}

export interface AgentArtifact {
  id: string;
  session_id: string;
  name: string;
  content_type: string;
  preview: string | null;
  key: string; // NoSQL document key: {sessionId}:{name}
}

export interface AgentMessage extends ChatMessage {
  tool_calls: AgentToolCall[] | null;
  artifacts: AgentArtifact[] | null;
}

export type AgentErrorCode =
  | "upstream_overloaded"
  | "provider_timeout"
  | "agent_error"
  | "unknown";

// SSE event types streamed from backend to frontend
export type SSEEvent = (
  | { type: "delta"; content: string }
  | { type: "tool_start"; id: string; name: string; input: string }
  /** Fine-grained tool status for UI (e.g. output size); follows tool_start / alongside tool_end */
  | { type: "tool_progress"; id: string; name: string; message: string }
  | { type: "tool_end"; id: string; name: string; status: "success" | "failed"; output: string }
  | { type: "artifact"; id: string; name: string; content_type: string; preview: string | null; key: string }
  | { type: "status"; status: string; detail?: string }
  | { type: "brand_not_ready"; missing: BrandReadinessMissingKey[] }
  | { type: "done"; message_id: string }
  | { type: "error"; code: AgentErrorCode; message: string; retryable: boolean }
  | {
      type: "card";
      card_type: string;
      data: Record<string, unknown>;
      actions?: Array<{
        label: string;
        value: string;
        style?: "primary" | "secondary" | "danger";
      }>;
    }
  | { type: "quick_replies"; replies: Array<{ label: string; value: string }> }
  /** PRD-0032 Phase 7 — brand-context synthesis finished after a tool turn */
  | { type: "synthesis_learned"; execution_id: string; summary: string; ok: boolean }
) & { server_emitted_at_ms?: number };

// ── Brand Documents ──

export type BrandDocSlug =
  | "audit-config"
  | "audit-report"
  | "brand-context"
  | "voice"
  | "strategy"
  | "persona-research"
  | "pain-point-map"
  | "positioning-angles"
  | "page-strategy-inference"
  | "page-quality-criteria"
  | "page-signals-markdown"
  | "page-outline"
  | "page-wireframe-markdown"
  | "page-copy-json"
  | "page-copy-draft"
  | "page-copy-review"
  | "page-copy-markdown"
  | "page-html-draft"
  | "page-voice-review"
  | "page-grade-report"
  | "page-seo-optimized"
  | "research-keyword-master"
  | "research-strategy-overlay"
  | "research-execution-plan";

export type ResearchPackageStatus =
  | "not_started"
  | "generated"
  | "needs_review"
  | "approved"
  | "superseded";

export type ResearchPackageStage =
  | "market_research_report"
  | "keyword_master"
  | "strategy_overlay"
  | "execution_plan"
  | "persona_research"
  | "pain_point_map"
  | "positioning_angles"
  | "page_production";

export interface BrandDocumentPackageMeta {
  package_id: string;
  stage: ResearchPackageStage;
  status: ResearchPackageStatus;
  upstream_artifact_keys?: string[];
  upstream_document_keys?: string[];
  source_run_id?: string | null;
  revision_number?: number | null;
}

export interface BrandDocument {
  document_key: string;       // "{brandId}:{slug}:{timestamp_ms}"
  brand_id: string;
  slug: BrandDocSlug;
  timestamp_ms: number;
  content: string;            // JSON string or markdown
  source: "agent" | "human";
  task_id: string | null;
  model: string | null;
  skill_files: string[];
  metadata?: BrandDocumentPackageMeta | Record<string, unknown> | null;
  created_at: string;
}

/** Revision summary row (no content) — for history timeline display */
export interface BrandDocumentRevision {
  document_key: string;
  timestamp_ms: number;
  source: "agent" | "human";
  model: string | null;
  task_id: string | null;
  created_at: string;
}

// ── Brand Artifact Documents ──

export type BrandArtifactType = "voice" | "strategy";
export type BrandArtifactStatus = "draft" | "needs_review" | "confirmed" | "archived";
export type BrandArtifactSectionKind = "text" | "markdown" | "list" | "object" | "json";
export type BrandArtifactSource = "agent" | "human" | "migration" | "system";

export type BrandArtifactListItem = string | Record<string, unknown>;
export type BrandArtifactListValue = BrandArtifactListItem[];
export type BrandArtifactStructuredValue =
  | string
  | BrandArtifactListValue
  | Record<string, unknown>;
export type BrandArtifactJsonValue =
  | string
  | string[]
  | Record<string, unknown>
  | Array<Record<string, unknown>>
  | null;

interface BaseBrandArtifactSection {
  id: string;
  label: string;
  kind: BrandArtifactSectionKind;
  patchable: boolean;
  summary?: string | null;
}

export type BrandArtifactSection =
  | (BaseBrandArtifactSection & {
    kind: "text" | "markdown";
    value: string;
  })
  | (BaseBrandArtifactSection & {
    kind: "list";
    value: BrandArtifactListValue;
  })
  | (BaseBrandArtifactSection & {
    kind: "object";
    value: Record<string, unknown>;
  })
  | (BaseBrandArtifactSection & {
    kind: "json";
    value: BrandArtifactJsonValue;
  });

export interface BrandArtifactManifest {
  title: string;
  sections: BrandArtifactSection[];
  meta?: Record<string, unknown> | null;
}

export interface BrandArtifactRevision {
  version: number;
  artifact_created_at: string;
  updated_at: string;
  source: BrandArtifactSource;
  confirmed_by_user_at: string | null;
}

export interface BrandArtifactDocument<TRuntime = Record<string, unknown>> {
  document_key: string;
  artifact_id: string;
  history_of: string | null;
  artifact_type: BrandArtifactType;
  brand_id: string;
  status: BrandArtifactStatus;
  manifest: BrandArtifactManifest;
  context_markdown: string;
  derived_runtime_snapshot: TRuntime;
  revision: BrandArtifactRevision;
  created_at: string;
  updated_at: string;
}

export interface BrandArtifactRevisionSummary {
  document_key: string;
  artifact_id: string;
  artifact_type: BrandArtifactType;
  status: BrandArtifactStatus;
  version: number;
  source: BrandArtifactSource;
  created_at: string;
  updated_at: string;
}

export type BrandArtifactPatchOperation =
  | {
    op: "replace_field";
    section_id: string;
    path: string;
    value: BrandArtifactStructuredValue | null;
  }
  | {
    op: "rewrite_text";
    section_id: string;
    path: string;
    value: string;
  }
  | {
    op: "append_list_item";
    section_id: string;
    path: string;
    value: string | Record<string, unknown>;
  }
  | {
    op: "remove_list_item";
    section_id: string;
    path: string;
    value: string | Record<string, unknown>;
  }
  | {
    op: "create_variant";
    section_id: string;
    path: string;
    label: string;
    value: BrandArtifactStructuredValue;
  };

export interface BrandArtifactPatch {
  artifact_id: string;
  artifact_type: BrandArtifactType;
  base_version: number;
  target_section_id: string | null;
  summary: string;
  rationale: string;
  operations: BrandArtifactPatchOperation[];
}

// ── Brand brain context (PRD-0032) — stored as JSON in hc_brand_documents slug `brand-context`

export interface BrandContextCompetitor {
  name: string;
  notes: string;
}

export interface BrandContextResonantTopic {
  topic: string;
  score: number;
}

export interface BrandContextCompetitorSignal {
  competitor: string;
  signal: string;
  date: string;
}

export interface BrandContextContentPattern {
  pattern: string;
  effectiveness: number;
}

export interface BrandContextLayer1Identity {
  brand_name: string;
  industry: string;
  voice: string;
  values: string[];
  target_persona: string;
  key_differentiator: string;
  website_url: string | null;
  linkedin_url: string | null;
  competitors: BrandContextCompetitor[];
  /**
   * Brand owner's primary call to action (brand profile → "Primary call to action"). Absent means
   * "visit the website". Read through backend/src/services/brand-primary-action.ts only.
   */
  primary_cta?: BrandPrimaryCallToAction | null;
}

export type BrandPrimaryActionKind = "visit" | "sign_up" | "join_waitlist" | "shop" | "subscribe" | "book_demo";
export interface BrandPrimaryCallToAction {
  kind: BrandPrimaryActionKind;
  /** null = the brand's website. */
  url: string | null;
  updated_at?: string;
}

export interface BrandContextLayer2Learned {
  top_performing_formats: string[];
  resonant_topics: BrandContextResonantTopic[];
  optimal_timing: Record<string, string>;
  competitor_signals: BrandContextCompetitorSignal[];
  content_patterns: BrandContextContentPattern[];
}

export interface BrandContextActiveThread {
  thread_id: string;
  purpose: string;
}

export interface BrandContextLayer3Working {
  current_campaign_id: string | null;
  pending_ideation: string[];
  active_threads: BrandContextActiveThread[];
  openclaw_thread_id: string | null;
  last_user_intent: string;
  last_skill_invoked: string | null;
  chat_summary: string;
  /** User corrections awaiting synthesis (optional; omitted in older docs) */
  pending_corrections?: string[];
}

export interface BrandContextMeta {
  version: number;
  updated_at: string;
  synthesis_run_id: string | null;
  admp_identity: string;
}

/** Full brand brain working memory — serialized to `brand-context` document */
export interface BrandContextJson {
  layer_1_identity: BrandContextLayer1Identity;
  layer_2_learned: BrandContextLayer2Learned;
  layer_3_working: BrandContextLayer3Working;
  meta: BrandContextMeta;
}

// ── Daily Task System ──

export type TaskCategory = "research" | "analytics" | "content" | "monitoring" | "reporting";
export type TaskRunStatus = "running" | "completed" | "failed" | "skipped" | "retrying" | "retried";
export type TaskFindingStatus = "new" | "acted" | "dismissed";
export type FindingType = "opportunity" | "threat" | "metric" | "content_idea" | "action_item";
export type WorkProductFamily =
  | "campaign_document"
  | "market_research_artifact"
  | "current_events_research_artifact"
  | "research_output"
  | "brand_document"
  | "agent_artifact"
  | "media_asset";
export type WorkProductReadiness =
  | "usable"
  | "usable_with_warnings"
  | "unlinked"
  | "diagnostic_only"
  | "failed"
  | "running";

export type OutputState =
  | BriefState
  | TaskFindingStatus
  | "generated"
  | "archived"
  | "unknown";

export const ACTIVE_HEAD_STATES = [
  "drafting",
  "voice_review",
  "in_review",
  "approved",
  "scheduled",
] as const satisfies readonly OutputState[];

export function isActiveHeadState(state: OutputState | null | undefined): boolean {
  if (!state) return false;
  return (ACTIVE_HEAD_STATES as readonly string[]).includes(state);
}

export const WORK_PRODUCT_WARNING_DEFINITIONS = {
  legacy_work_product_inferred: {
    label: "Inferred from legacy output",
    description: "This work product was inferred from legacy document metadata instead of an explicit work_products entry.",
  },
  native_record_collection_missing: {
    label: "Storage collection missing",
    description: "The work product does not include enough storage routing data to resolve its native record.",
  },
  native_record_missing: {
    label: "Native record missing",
    description: "The referenced native record could not be found in storage.",
  },
  native_record_read_failed: {
    label: "Native record read failed",
    description: "The native record lookup failed before trace details could be resolved.",
  },
  source_findings_truncated: {
    label: "Source findings truncated",
    description: "Only the first batch of source-run findings is included in this trace response.",
  },
  source_run_missing_for_finding: {
    label: "Finding source run missing",
    description: "A supporting finding references a source run that was not available in the trace response.",
  },
  source_run_unresolved: {
    label: "Source run unresolved",
    description: "A referenced source run could not be resolved for this brand.",
  },
  supporting_findings_truncated: {
    label: "Supporting findings truncated",
    description: "Only the first batch of supporting findings is included in this trace response.",
  },
  work_products_missing: {
    label: "Work products missing",
    description: "The run completed without explicit work products in its output.",
  },
  research_output_quality_partial: {
    label: "Research quality partial",
    description: "This research output is usable, but its recorded data quality is only partial.",
  },
  research_output_quality_weak: {
    label: "Research quality weak",
    description: "This research output is present, but its recorded data quality is weak and needs operator review.",
  },
  market_research_report_missing: {
    label: "Research report missing",
    description: "The market research artifact exists, but the final report body is empty or missing.",
  },
  market_research_findings_missing: {
    label: "Research findings missing",
    description: "The market research artifact exists, but it does not include persisted findings.",
  },
  current_events_research_report_missing: {
    label: "Current-events report missing",
    description: "The current-events research artifact exists, but its readable report is missing.",
  },
  current_events_research_sources_missing: {
    label: "Current-events sources missing",
    description: "The current-events research artifact has no readable dated sources for review.",
  },
  current_events_research_needs_attention: {
    label: "Current-events research needs attention",
    description: "The requested research could not meet its evidence contract, so campaign guidance was not completed.",
  },
  agent_artifact_preview_only: {
    label: "Artifact preview only",
    description: "The agent artifact only has preview text, so the full content should be downloaded or regenerated before relying on it.",
  },
  agent_artifacts_omitted_from_brand_index: {
    label: "Artifacts omitted from brand index",
    description: "Some agent artifacts are still user-scoped or malformed, so they are not listed in the active brand Outputs index.",
  },
} as const;

export type WorkProductWarningCode = keyof typeof WORK_PRODUCT_WARNING_DEFINITIONS;
export type WorkProductWarning = WorkProductWarningCode | string;

export function workProductWarningDefinition(code: string): { label: string; description: string } {
  return WORK_PRODUCT_WARNING_DEFINITIONS[code as WorkProductWarningCode] ?? {
    label: code.replace(/_/g, " ").replace(/\b\w/g, (char) => char.toUpperCase()),
    description: "Unrecognized warning code.",
  };
}

export interface WorkProductRef {
  id: string;
  key: string;
  family: WorkProductFamily;
  output_type: string;
  brand_id: string;
  source_run_id: string | null;
  task_slug: string;
  /** Persisted Action presentation for the producing Job; null for legacy sources. */
  source_job_label?: string | null;
  created_at: string;
  title: string;
  readiness: WorkProductReadiness;
  route_path?: string | null;
  storage_collection?: string | null;
  document_key?: string | null;
  artifact_key?: string | null;
  storage_key?: string | null;
  campaign_id?: string | null;
  brief_id?: string | null;
  package_id?: string | null;
  package_stage?: ResearchPackageStage | null;
  revision_number?: number | null;
  warnings?: WorkProductWarning[];
}

export interface WorkProductDetail {
  work_product: WorkProductRef;
  content: string | null;
  output_document?: OutputDocument | null;
  state?: OutputState | null;
  grounding?: OutputGroundingSummary | null;
  metadata: Record<string, unknown> | null;
  native_record: Record<string, unknown> | null;
  trust_gate?: TrustGateResult | null;
}

export interface WorkProductIndexFamilySummary {
  family: WorkProductFamily;
  supported: boolean;
  count: number;
}

export type WorkProductIndexOmissionReason = "not_brand_scoped" | "missing_identity" | "failed_contract_check";

export interface WorkProductIndexOmissionSummary {
  family: WorkProductFamily;
  reason: WorkProductIndexOmissionReason;
  count: number;
}

export interface WorkProductIndexResponse {
  outputs: WorkProductRef[];
  families: WorkProductIndexFamilySummary[];
  omissions: WorkProductIndexOmissionSummary[];
  index_strategy: "computed_on_read";
  warnings: WorkProductWarning[];
}

export interface TaskRunTraceRunNode {
  id: string;
  brand_id: string;
  task_slug: string;
  status: TaskRunStatus;
  run_scope?: string | null;
  rerun_of_run_id?: string | null;
  campaign_id?: string | null;
  brief_id?: string | null;
  started_at: string;
  completed_at: string | null;
  readiness: WorkProductReadiness;
  /** Present on current trace responses; optional for persisted/test legacy graphs. */
  presentation?: TaskRunPresentation;
  route_path: string;
}

/**
 * Tenant-safe, recorded workflow activity for one Run. This intentionally
 * excludes workflow context, prompts, node output, credentials, and raw error
 * text; the Run Trace is observability, not an internal workflow API proxy.
 */
export interface TaskRunWorkflowHistoryEntry {
  node_id: string;
  started_at: string;
  completed_at: string | null;
  status: "success" | "failed" | "skipped";
  cost_cents: number | null;
  error_class: "transient" | "permanent" | "quota" | "auth" | "unknown" | null;
}

export type TaskRunWorkflowHistoryState = "recorded" | "not_recorded" | "unavailable";

export interface WorkProductNativeRecordTrace {
  family: WorkProductFamily;
  key: string;
  storage_collection: string | null;
  exists: boolean;
  record_type: string | null;
  title: string | null;
  created_at: string | null;
  metadata: Record<string, unknown> | null;
  warnings: WorkProductWarning[];
}

export interface TaskRunTraceGraph {
  run: TaskRunTraceRunNode;
  work_products: WorkProductRef[];
  native_records: WorkProductNativeRecordTrace[];
  supporting_findings: TaskRunBriefEvidence[];
  source_runs: TaskRunTraceRunNode[];
  warnings: WorkProductWarning[];
  resolver_warnings?: TaskRunResolverWarning[];
  trust_gate?: TrustGateResult | null;
  run_config?: TaskRunConfigObservability | null;
  /** Recorded only when the authorized Run has a matching workflow instance. */
  /** Optional only for historic/test trace graphs; current API responses include both fields. */
  workflow_history?: TaskRunWorkflowHistoryEntry[];
  workflow_history_state?: TaskRunWorkflowHistoryState;
}

/** Verified collection lineage; provider payloads remain private. */
export interface TaskRunCollection {
  dataset?: { dataset_id: string; query_id: string; query_version: number; row_count: number; completeness: "complete" | "partial"; collected_at: string; requested_window: { from: string; to: string }; route_path: string };
  state: "collected" | "partial" | "evidence_unavailable";
  source_path: string;
  status_counts?: { succeeded: number; partial: number; failed: number };
  children: Array<{ run_id: string; status: string; rows: number | null; requested_start: string | null; requested_end: string | null }>;
  artifacts: Array<{ output_id: string; artifact_id: string; run_id: string; route_path: string }>;
}

export type TaskRunOperatorState =
  | "evidence_unavailable"
  | "collection_partial"
  | "running"
  | "completed_useful"
  /**
   * Finished without producing anything. For a workflow-backed run the label names the node
   * it ended on, which is what the audit found missing: a no-op that does not say where it
   * stopped reads as ordinary success (PRD-0155 FR-5).
   */
  | "completed_noop"
  | "failed"
  | "stale"
  | "skipped";

export interface TaskRunStateProjection {
  operator_state: TaskRunOperatorState;
  label: string;
  summary: string | null;
  execution_outcome: string | null;
  no_op_reason: string | null;
  prerequisite: string | null;
  workflow_instance_id: string | null;
  workflow_status: string | null;
  current_node_id: string | null;
  stale_run: boolean;
}

/**
 * Safe, bounded presentation metadata for a task run.
 *
 * This is derived from the task-definition snapshot at the API boundary.  It
 * lets list surfaces name marketer work consistently without receiving the
 * raw execution output, prompt, or executor configuration.
 */
export interface TaskRunPresentation {
  job_label: string;
  brief_label: string;
  result_label: string;
  evidence_label: string;
  /** Persisted Action contract metadata; not inferred from the current catalog. */
  result_view?: "standard" | "market_research_review" | "current_events_campaign_research" | "social_listening_action";
  recovery_kinds?: Array<"none" | "retry" | "rerun" | "link">;
  /** Present only when a persisted Action snapshot cannot be safely decoded. */
  compatibility_state?: "action_snapshot_invalid";
}

export interface TaskTemplate {
  slug: string;
  name: string;
  description: string;
  category: TaskCategory;
  tool: string;
  input_schema: Record<string, unknown>;
  output_schema: Record<string, unknown>;
  default_schedule: string;
  built_in: boolean;
  version: number;
}

export interface BrandTaskConfig {
  brand_id: string;
  task_slug: string;
  enabled: boolean;
  schedule: string;
  params: Record<string, unknown>;
  last_run_at: string | null;
  created_at: string;
}

export interface TaskRun {
  id: string;
  brand_id: string;
  task_slug: string;
  run_date: string;
  run_scope?: string | null;
  rerun_of_run_id?: string | null;
  status: TaskRunStatus;
  skip_reason?: string | null;
  input: Record<string, unknown> | null;
  output: Record<string, unknown> | null;
  findings_count: number;
  duration_ms: number | null;
  tokens_used: number | null;
  cost_credits: number;
  thread_id: string | null;
  started_at: string;
  completed_at: string | null;
  error?: string | null;
  workflow_instance_id?: string | null;
  campaign_id: string | null;
  brief_id: string | null;
  campaign_title?: string | null;
  collection?: TaskRunCollection | null;
  state_projection?: TaskRunStateProjection | null;
  presentation?: TaskRunPresentation | null;
  query_diagnostics?: TaskRunQueryDiagnostic[];
  trust_summary?: TaskRunTrustSummary;
  trust_gate?: TrustGateResult | null;
  run_config?: TaskRunConfigObservability | null;
  work_products?: WorkProductRef[];
  readiness?: WorkProductReadiness;
  market_research_review?: MarketResearchReview | null;
  /** Verified read-only SEO outcome; persisted execution status remains unchanged. */
  seo_research?: import("./seo-run.ts").SeoRunStatus | null;
  /** Present for agent chat runs (`task_slug === "agent_chat"`) when tool metadata was persisted. */
  tool_calls?: unknown;
}

/**
 * Privacy-bounded row returned by `GET /api/task-runs`. Activity does not need
 * raw prompts or executor output; use `presentation` and outcome projection.
 */
export type TaskRunListStateProjection = Omit<TaskRunStateProjection, "summary">;

export type TaskRunListItem = Omit<TaskRun, "input" | "output" | "tool_calls" | "state_projection" | "run_config"> & {
  state_projection?: TaskRunListStateProjection | null;
};

export type MarketResearchReviewState = "working" | "ready_to_review" | "needs_attention";
export type MarketResearchReviewReason =
  | "job_failed"
  | "canonical_output_missing"
  | "canonical_output_malformed"
  | "canonical_output_unreadable"
  | "analysis_blocked";

export interface MarketResearchReview {
  state: MarketResearchReviewState;
  reason: MarketResearchReviewReason | null;
  output_id: string | null;
  route_path: string | null;
}

export type TrustIssueKind = "retrieval" | "source" | "synthesis";

export interface TrustIssue {
  kind: TrustIssueKind;
  code: string;
  label: string;
  detail: string;
}

export interface TaskFindingProvenance {
  query: string | null;
  source_type: string | null;
  source_url: string | null;
  domain: string | null;
  author: string | null;
  published_at: string | null;
  platform: string | null;
}

export interface BrandStats {
  pending_approvals: number | null;
  runs_today: number | null;
  failed_24h: number | null;
  active_campaigns: number | null;
  _partial?: boolean;
}

// Content approval summary (PRD-0083 FR-12) — shared source of truth for
// content-pipeline draft approval progress, consumed by the Dashboard.
export interface ContentRunApprovalSummary {
  runId: string;
  total: number;
  draft: number;
  approved: number;
  rejected: number;
  published: number;
  statusLabel: string;
}

export interface ContentApprovalSummary {
  pendingDrafts: number;
  approvedDrafts: number;
  rejectedDrafts: number;
  runs: ContentRunApprovalSummary[];
}

export interface TaskFinding {
  id: string;
  run_id: string;
  brand_id: string;
  task_slug: string;
  type: FindingType;
  title: string;
  body: string | null;
  score: number;
  status: TaskFindingStatus;
  metadata: Record<string, unknown> | null;
  acted_at: string | null;
  created_at: string;
  provenance?: TaskFindingProvenance;
  trust_issues?: TrustIssue[];
}

export interface TaskRunQueryDiagnostic {
  query: string;
  findings_count: number;
  top_domains: string[];
  source_types: string[];
}

export interface TaskRunTrustSummary {
  retrieval_count: number;
  source_count: number;
  synthesis_count: number;
  issues: TrustIssue[];
}

export type TrustGateState = "healthy" | "degraded" | "blocked";

export type TrustGateReasonCode =
  | "missing_source_url"
  | "partial_provenance"
  | "stale_duplicate"
  | "rejected_evidence"
  | "legacy_evidence_unrefreshed"
  | "insufficient_decision_ready_evidence";

export interface TrustGateResult {
  state: TrustGateState;
  reason_codes: TrustGateReasonCode[];
  total_candidates: number;
  selected_candidates: number;
  decision_ready_candidates: number;
  degraded_candidates: number;
  suppressed_duplicate_count: number;
  rejected_evidence_count: number;
  rollout_policy: "degraded_until_refreshed";
}

export interface TaskRunConfigObservability {
  provider: string | null;
  model: string | null;
  timeout_ms: number | null;
  trust_gate_state: TrustGateState | null;
  rejected_evidence_count: number | null;
}

export interface TaskRunBriefEvidence {
  finding_id: string;
  run_id: string | null;
  title: string;
  type: FindingType;
  score: number;
  query: string | null;
  source_type?: string | null;
  source_url: string | null;
  source_label?: string | null;
  source_access?: "external" | "internal" | "unavailable" | null;
  domain: string | null;
  author?: string | null;
  published_at: string | null;
  platform?: string | null;
  provenance_status?: "internal" | "complete" | "partial";
  /** Human-safe grouping for result evidence; it never invents a source URL. */
  presentation_kind?: "web_citation" | "search_signal" | "generated_observation" | "partial_or_unknown";
  presentation_group_label?: string;
  presentation_group_description?: string;
  missing_provenance_fields?: string[];
  trust_issues?: TrustIssue[];
  /** Operator triage state (PRD-0084) — lets the report reflect Done/Dismiss across reloads. */
  status?: TaskFindingStatus | null;
}

export interface TaskRunResolverWarning {
  reason: string;
  message: string;
  details: Record<string, unknown>;
}

export type TaskRunBriefSectionKey =
  | "top_opportunities"
  | "threats_competitor_moves"
  | "content_ideas"
  | "metrics"
  | "action_items";

export interface TaskRunBriefSectionEvidence {
  section_key: TaskRunBriefSectionKey;
  label: string;
  anchor?: string;
  findings: TaskRunBriefEvidence[];
}

export interface TaskRunBriefDetail {
  kind: "daily_brief" | "campaign_brief";
  run_id: string;
  brand_id: string;
  task_slug: string;
  run_date: string;
  title: string;
  content: string;
  findings_count: number;
  created_at: string | null;
  brief_id: string | null;
  campaign_id: string | null;
  campaign_title?: string | null;
  document_key?: string | null;
  document_type?: BriefDocType | "daily_brief";
  output_route_path?: string | null;
  supporting_findings?: TaskRunBriefEvidence[];
  section_evidence?: TaskRunBriefSectionEvidence[];
  resolver_warnings?: TaskRunResolverWarning[];
  trust_gate?: TrustGateResult | null;
}

// ── Brand Skills ──

export interface BrandSkill {
  id: string;
  brand_id: string;
  skill_slug: string;
  skill_name: string;
  content: string;
  version: number;
  based_on_default: number;
  updated_by: "user" | "ai";
  created_at: string;
  updated_at: string;
}

// ── Playbooks ──

export interface Playbook {
  id: string;
  name: string;
  description: string;
  steps: PlaybookStep[];
}

export interface PlaybookStep {
  day: number;
  prompt_template: string;
  platforms: Platform[];
}

// ── API Response Types ──

export interface ApiResponse<T> {
  data: T;
  error?: never;
}

export interface ApiError {
  data?: never;
  error: {
    message: string;
    code: string;
    details?: unknown;
  };
}

export type ApiResult<T> = ApiResponse<T> | ApiError;

// ── Brand Extraction ──

export interface BrandExtractionResult {
  brand_name: string;
  industry: string;
  target_audience: string;
  /**
   * Overall tone phrase. The backend always fills it (from descriptors or a
   * default); optional so a newer frontend tolerates an older API during deploys.
   */
  tone?: string;
  tone_descriptors: string[];
  vocabulary: string[];
  positioning_statement: string;
  example_voice_samples: string[];
}

// ── Pipeline Input/Output Contracts ──

export interface IntakeOutput {
  summary: string;
  key_topics: string[];
  sentiment: string;
  potential_angles: string[];
}

export interface AnchorContent {
  title: string;
  body: string;
  format: string;
  word_count: number;
  key_points: string[];
  hashtags: string[];
}

export interface RepurposedContent {
  platform: Platform;
  content: string;
  format: string;
  character_count: number;
  hashtags: string[];
  media_suggestions: string[];
}

export interface WeeklyDigest {
  summary: string;
  top_performers: { piece_id: string; platform: Platform; headline: string }[];
  insights: string[];
  recommendations: string[];
  topic_scores: { topic: string; avg_engagement: number }[];
}

// ── Content Control Plane ──

export type ConcurrencyPolicy = "coalesce_if_active" | "skip_if_active" | "always_enqueue";
export type CatchUpPolicy = "skip_missed" | "enqueue_one";
export type RoutineStatus = "active" | "paused" | "archived";

export type CampaignStatus = "draft" | "active" | "paused" | "completed" | "archived";
// PRD-0072 broadens campaign surfaces from portfolio-specific literals to
// generic per-brand kinds like "blog", "linkedin", and "youtube".
// Legacy labels may still appear in stored compatibility data, so Surface stays
// open-ended rather than enumerating every historical string.
export type Surface = string & {};

// ── Surfaces Primitive (PRD-0051) ──

export type SurfaceCategory =
  | "owned_publication"
  | "participatory_identity"
  | "hosted_listing"
  | "earned_placement"
  | "algorithmic_discovery";

export type SurfaceStatus = "proposed" | "active" | "warming_up" | "paused" | "blocked" | "retired";

export type SurfaceControlLevel = "owned" | "claimed" | "participated" | "observed" | "earned";

export type SurfaceHealthStatus = "unknown" | "healthy" | "attention" | "blocked";

export type WarmUpPhase = "cold" | "warming" | "established";

// Surface config shapes by category (subset for frontend use)
export type OwnedPublicationConfig = {
  audience_count?: number;
  primary_formats?: string[];
  cadence?: string;
  analytics_source?: string;
  editorial_state?: "active" | "paused" | "planning";
};

export type ParticipatoryIdentityConfig = {
  warm_up_phase: WarmUpPhase;
  reputation_score?: number;
  karma?: number;
  account_age_days?: number;
  max_posts_per_day?: number;
  allowed_subreddits?: string[];
  human_review_required?: boolean;
};

export type HostedListingConfig = {
  claim_status?: "unclaimed" | "claimed" | "verified";
  listing_completeness?: number;
  current_rating?: number;
  review_count?: number;
  conversion_url?: string;
};

export type EarnedPlacementConfig = {
  placement_type?: string;
  publisher_domain?: string;
  relationship_contact?: string;
  authority_score?: number;
  sentiment?: "positive" | "neutral" | "negative";
  backlink_url?: string;
};

export type AlgorithmicDiscoveryConfig = {
  tracked_queries?: string[];
  target_url?: string;
  current_rank?: number;
  competitors_tracked?: string[];
  optimization_levers?: string[];
  check_cadence?: string;
};

export type SurfaceConfig =
  | OwnedPublicationConfig
  | ParticipatoryIdentityConfig
  | HostedListingConfig
  | EarnedPlacementConfig
  | AlgorithmicDiscoveryConfig
  | Record<string, unknown>;

export interface BrandSurface {
  id: string;
  brand_id: string;
  parent_surface_id: string | null;
  kind: string;
  category: SurfaceCategory;
  display_name: string;
  external_id: string | null;
  canonical_url: string | null;
  status: SurfaceStatus;
  control_level: SurfaceControlLevel;
  health_status: SurfaceHealthStatus;
  priority: number;
  config: SurfaceConfig;
  notes: string | null;
  last_observed_at: string | null;
  last_activity_at: string | null;
  created_at: string;
  updated_at: string;
  /** Derived from (category, kind) — not stored in DB */
  capabilities?: string[];
}

export type IdentityKind = "brand" | "person" | "agent" | "shared";
export type IdentityStatus = "active" | "warming_up" | "paused" | "blocked" | "retired";

export interface Identity {
  id: string;
  brand_id: string;
  kind: IdentityKind;
  display_name: string;
  platform_kind: string;
  external_id: string | null;
  canonical_url: string | null;
  status: IdentityStatus;
  source_identity_id: string | null;
  credentials_ref: string | null;
  config: Record<string, unknown>;
  created_at: string;
  updated_at: string;
}

/**
 * Per-brand voice keys (PRD-0072 FR-1). Generic role-based scheme — the brand
 * dimension is carried by the storage key (`{brandId}:{voiceKey}`), so keys
 * describe a role, not a specific company.
 */
export const VOICE_KEYS = ["brand", "founder_personal"] as const;
export type VoiceKey = (typeof VOICE_KEYS)[number];

export interface VoiceProfile {
  id: string;
  voice_key: VoiceKey;
  brand_id: string;
  tone: string;
  tone_descriptors: string[];
  vocabulary: string[];
  guidelines: string;
  example_posts: string[];
  created_at: string;
  updated_at: string;
}

export interface VoicePatch {
  patch_type:
    | "tone"
    | "tone_descriptors_add"
    | "tone_descriptors_remove"
    | "vocabulary_add"
    | "vocabulary_remove"
    | "guidelines"
    | "example_add"
    | "example_remove";
  value: string;
  voice_key: VoiceKey;
  source?: "agent_chat" | "agent_workbench" | "human";
  actor_id?: string | null;
  applied_at: string;
}
export type CadenceSource = "auto" | "manual" | "adhoc";
export type DayOfWeek = "Sun" | "Mon" | "Tue" | "Wed" | "Thu" | "Fri" | "Sat";
export type ReviewAuditAction =
  | "approve_opened"
  | "nudge_opened"
  | "reject"
  | "published_url_submitted"
  | "rewrite_requested"
  // PRD-0190 publish handoff
  | "publish_ready"
  | "approval_revoked";

export type BriefState =
  | "ideation"
  | "researching"
  | "briefed"
  | "drafting"
  | "voice_review"
  | "in_review"
  | "approved"
  | "scheduled"
  | "published"
  | "blocked"
  | "cancelled";

export type OperatorWorkStatus = "ok" | "error";
export type OperatorWorkBucket = "needs_approval" | "blocked" | "pending_gate";
export type OperatorWorkSeverity = "blocker" | "warning" | "info";

export interface OperatorWorkTrustAlert {
  id: string;
  severity: OperatorWorkSeverity;
  title: string;
  detail: string;
  code?: string | null;
}

export interface OperatorWorkItem {
  id: string;
  bucket: OperatorWorkBucket;
  title: string;
  channel: string | null;
  state: BriefState | "pending_gate";
  next_action: string;
  created_at: string;
  updated_at: string;
  brief_id: string | null;
  campaign_id: string | null;
  gate_id: string | null;
  trust_alerts: OperatorWorkTrustAlert[];
}

export interface OperatorWorkResponse {
  status: OperatorWorkStatus;
  items: OperatorWorkItem[];
  counts: Record<OperatorWorkBucket, number>;
}

export type SourceScheduleWeekday =
  | "sunday"
  | "monday"
  | "tuesday"
  | "wednesday"
  | "thursday"
  | "friday"
  | "saturday";

export interface DailySourceScheduleInput {
  frequency: "daily";
  /** Local wall-clock minute in strict 24-hour HH:mm form. */
  time: string;
  /** IANA timezone used to interpret the local wall-clock minute. */
  timezone: string;
}

export interface WeeklySourceScheduleInput {
  frequency: "weekly";
  weekday: SourceScheduleWeekday;
  /** Local wall-clock minute in strict 24-hour HH:mm form. */
  time: string;
  /** IANA timezone used to interpret the local wall-clock minute. */
  timezone: string;
}

/** User-facing source schedules supported by the first release. */
export type SourceScheduleInput = DailySourceScheduleInput | WeeklySourceScheduleInput;

/** Saved plan and actual attempt evidence; a planned time is not a dispatch guarantee. */
export interface PublicSourceSchedule {
  id: string;
  action_slug: string;
  revision: number;
  settings: SourceScheduleInput | null;
  enabled: boolean;
  enrolled: boolean;
  next_scheduled_at: string | null;
  last_attempt: {
    id: string;
    status: string;
    started_at: string | null;
    completed_at: string | null;
  } | null;
}

export interface PublicSourceAnalyticsSchedule {
  /** A persisted report bundle exists independently of its optional Schedule. */
  action_saved?: boolean;
  resource_id?: string;
  surface_id?: string | null;
  history?: { start: string; end: string; earliest: string; maximum_days: number;
    pending: boolean; requested_start: string | null; requested_end: string | null };
  assignment_id: string;
  connection_id: string;
  capability: ProviderCapability;
  supported: boolean;
  blockers: string[];
  schedule: PublicSourceSchedule | null;
  last_collection_success_at: string | null;
}

export type BriefDocType = "brief" | "draft" | "voice_review" | "published";
export type BriefAssignee = "agent" | "user" | null;

export interface Routine {
  id: string;
  brand_id: string;
  slug: string;
  title: string;
  cron_expression: string;
  timezone: string;
  concurrency_policy: ConcurrencyPolicy;
  catch_up_policy: CatchUpPolicy;
  status: RoutineStatus;
  params: Record<string, unknown>;
  /** Present for source-scoped schedules; omitted/null retains legacy slug dispatch. */
  action_slug?: string | null;
  /** Owned resource-assignment binding for a source-scoped schedule. */
  source_assignment_id?: string | null;
  /** Monotonic edit/claim fence. Legacy payloads may omit the database default of zero. */
  schedule_revision?: number;
  /** Current claim ownership fields; both are null when the schedule is unclaimed. */
  claim_token?: string | null;
  claim_expires_at?: string | null;
  /** Pending occurrence kind. Legacy payloads may omit the database default of cron. */
  next_trigger?: "cron" | "resume";
  next_fire_at: string | null;
  last_fired_at: string | null;
  created_at: string;
  updated_at: string;
}

export type TaskRunMode = "manual" | "scheduled" | "manual_and_scheduled";
export type TaskLifecycle = "pre_launch" | "existing_brand" | "any";
export type TaskCatalogCategory = "intelligence" | "content" | "review" | "system" | "research";
export type ToolCatalogCategory = "search" | "reader" | "llm" | "brand" | "workflow";

export interface TaskCatalogSchedule {
  id?: string;
  revision?: number;
  exists: boolean;
  enabled: boolean;
  status: RoutineStatus | null;
  cron_expression: string | null;
  timezone: string | null;
  next_fire_at: string | null;
  last_fired_at: string | null;
}

export interface TaskCatalogLastRun {
  id: string;
  status: TaskRunStatus;
  started_at: string;
  completed_at: string | null;
  duration_ms: number | null;
  findings_count: number;
  skip_reason?: string | null;
  collection?: TaskRunCollection | null;
  state_projection?: TaskRunStateProjection | null;
}

export type TaskCatalogInputControl = "text" | "textarea" | "number" | "checkbox";

export interface TaskCatalogParameterSpec {
  type: "string" | "number" | "boolean" | "object" | "array";
  required?: boolean;
  required_for?: Array<"manual" | "scheduled">;
  description: string;
  example?: unknown;
  source?: "operator_input" | "derived_from_slug" | "routine_configuration";
  label?: string;
  placeholder?: string;
  control?: TaskCatalogInputControl;
  minimum?: number;
  maximum?: number;
  integer?: boolean;
}

/** Safe, serializable subset of the source-controlled task definition. */
export interface TaskCatalogManualLaunch {
  title: string;
  description: string;
  submit_label: string;
  fields: string[];
}

export interface TaskCatalogResearchSource {
  id: string;
  label: string;
  purpose: string;
}

export interface TaskCatalogResearchPolicy {
  prompt_template_id: string;
  prompt_template_version: number;
  source_policy_id: string;
  source_policy_version: number;
  sources: TaskCatalogResearchSource[];
  evidence_requirements: string[];
}

export interface TaskCatalogExecutionBinding {
  executor_slug: string;
  workflow_id?: string;
  output_family?: OutputFamily;
  output_metadata?: OutputMetadataSchema;
  output_type?: string;
  output_adapter?: string;
  output_key?: string;
}

export interface TaskCatalogObservability {
  run_label: string;
  output_label: string;
}

/** UI-safe presentation labels owned by the canonical backend task definition. */
export interface TaskCatalogMarketerPresentation {
  brief_label: string;
  result_label: string;
  evidence_label: string;
}

/** Versioned, allow-listed Action contract. Never contains executor or prompt data. */
export type TaskCatalogActionLaunchField =
  | { key: string; type: "string" | "textarea"; label: string; help: string; required: boolean; default?: string; max_length: number }
  | { key: string; type: "number"; label: string; help: string; required: boolean; default?: number; minimum?: number; maximum?: number; integer?: boolean }
  | { key: string; type: "boolean"; label: string; help: string; required: boolean; default?: boolean }
  | { key: string; type: "select"; label: string; help: string; required: boolean; default?: string; options: Array<{ value: string; label: string }> };

export interface TaskCatalogActionDescriptor {
  contract_version: 1;
  definition_version: number;
  presentation: TaskCatalogMarketerPresentation & { title: string; description: string };
  launch: { title: string; description: string; submit_label: string; fields: TaskCatalogActionLaunchField[] } | null;
  prerequisites: Array<{ capability: "brand_context"; ready: boolean; explanation: string }>;
  result: { label: string; view: "standard" | "market_research_review" | "current_events_campaign_research" | "social_listening_action" };
  /** Bounded recovery controls authorized by the persisted Action contract. */
  recovery: { kinds: Array<"none" | "retry" | "rerun" | "link"> };
}

export interface TaskCatalogItem {
  slug: string;
  category: TaskCatalogCategory;
  lifecycle: TaskLifecycle;
  run_mode: TaskRunMode;
  manual_run_supported: boolean;
  scheduled_run_supported: boolean;
  /** Versioned allow-listed browser contract. */
  action: TaskCatalogActionDescriptor;
  schedule: TaskCatalogSchedule;
  last_run: TaskCatalogLastRun | null;
}

export interface ToolCatalogItem {
  slug: string;
  title: string;
  category: ToolCatalogCategory;
  description: string;
  use_cases: string[];
  integration_notes: string[];
}

export interface CampaignActionPlanDraft {
  task_slug: string;
  definition_version: number;
  title: string;
  objective: string;
  params: Record<string, unknown>;
  starts_at: string | null;
  ends_at: string | null;
}
export interface CampaignActionPlan extends CampaignActionPlanDraft {
  id: string;
  campaign_id: string;
  brand_id: string;
  status: "planned" | "archived";
  revision: number;
  created_at: string;
  updated_at: string;
}

export interface Campaign {
  id: string;
  brand_id: string;
  title: string;
  goal: string | null;
  /** Optional link back to the brand strategy pillar this campaign serves. */
  strategy_pillar: string | null;
  deadline: string | null;
  starts_at: string | null;
  ends_at: string | null;
  status: CampaignStatus;
  voice_overrides: Record<string, unknown> | null;
  created_by_user_id: string;
  created_at: string;
  updated_at: string;
}

export interface Brief {
  id: string;
  campaign_id: string;
  brand_id: string;
  title: string;
  platform: string | null;
  format: string | null;
  state: BriefState;
  assignee: BriefAssignee;
  voice_overrides: Record<string, unknown> | null;
  brief_locked_by: string | null;
  brief_locked_at: string | null;
  due_at: string | null;
  blocked_reason: string | null;
  scheduled_at?: string | null;
  surface?: Surface | null;
  utm_campaign?: string | null;
  published_url?: string | null;
  published_at?: string | null;
  cadence_source?: CadenceSource | null;
  source_finding_id?: string | null;
  content_origin?: ContentOrigin | null;
  review_trust_state?: ReviewTrustState | null;
  rendered_asset_url?: string | null;
  thumbnail_url?: string | null;
  preview_url?: string | null;
  provenance_alerts?: ReviewTrustAlert[];
  grounding?: OutputGroundingSummary | null;
  created_at: string;
  updated_at: string;
}

export interface BriefEvent {
  id: string;
  brief_id: string;
  campaign_id: string;
  brand_id: string;
  from_state: BriefState | null;
  to_state: BriefState;
  actor: string;
  reason: string | null;
  metadata: Record<string, unknown> | null;
  created_at: string;
}

export interface BriefDocument {
  document_key: string;
  brief_id: string;
  campaign_id: string;
  brand_id: string;
  doc_type: BriefDocType;
  revision: number;
  content: string;
  output_document?: OutputDocument | null;
  grounding?: OutputGroundingSummary | null;
  metadata: Record<string, unknown> | null;
  trust_gate?: TrustGateResult | null;
  created_by: string;
  created_at: string;
  updated_at: string;
}

export type ContentPacketStatus =
  | "draft"
  | "in_review"
  | "pending_approval"
  | "approved"
  | "scheduled"
  | "published"
  | "blocked";

export type ContentOrigin =
  | "generated_from_source"
  | "generated_from_brand_context"
  | "operator_curated_packet"
  | "imported_external_draft"
  | "legacy_unknown";

export type ReviewTrustState =
  | "source_backed"
  | "manual_reviewed"
  | "brand_context_only"
  | "broken_lineage"
  | "claim_risk"
  | "legacy_unknown";

export interface ContentPacketSourceRef {
  label: string | null;
  url: string;
}

export interface ContentPacketDecision {
  at: string;
  actor: string | null;
  action: string;
  reason: string | null;
  metadata: Record<string, unknown> | null;
}

export interface ContentPacketDocumentData {
  status: ContentPacketStatus;
  angle_brief: string | null;
  review_report: string | null;
  approval_packet: string | null;
  source_refs: ContentPacketSourceRef[];
}

export interface ContentPacket {
  brief_id: string;
  campaign_id: string;
  brand_id: string;
  title: string;
  surface: Surface | null;
  content_origin?: ContentOrigin | null;
  brief_state: BriefState;
  draft_body: string;
  output_document?: OutputDocument | null;
  grounding?: OutputGroundingSummary | null;
  angle_brief: string | null;
  review_report: string | null;
  approval_packet: string | null;
  source_refs: ContentPacketSourceRef[];
  status: ContentPacketStatus;
  decision_history: ContentPacketDecision[];
  document_key: string;
  review_path: string;
  campaign_path: string;
  output_path: string;
  created_at: string;
  updated_at: string;
}

export interface BrandAuditScore {
  id: string;
  brand_id: string;
  audited_at: string;
  overall_score: number;
  metadata: Record<string, unknown> | null;
  created_at: string;
}

export interface CadenceSlot {
  surface: Surface;
  days: DayOfWeek[];
  slots_per_day: number;
  lead_days: number;
}

export interface CadenceConfig {
  id: string;
  brand_id: string;
  slots: CadenceSlot[];
  status: "active" | "paused" | "archived";
  started_at: string | null;
  paused_at: string | null;
  created_at: string;
  updated_at: string;
}

export interface IdeaScore {
  relevance: number;
  novelty: number;
  signal: number;
  authenticity: number;
  differentiation: number;
  total: number;
  calibration_version: string;
}

export type IdeaSource = "intel" | "social" | "corpus" | "audit" | "chat" | "manual";

export interface CampaignIdeaProvenance {
  query?: string | null;
  source_query?: string | null;
  source_url?: string | null;
  source_domain?: string | null;
  source_type?: string | null;
  platform?: string | null;
  author?: string | null;
  published_at?: string | null;
  posted_at?: string | null;
  body_kind?: "full" | "snippet" | "summary" | "internal" | null;
  provenance_status?: "complete" | "partial" | "internal" | null;
}

/** PRD-0021 campaign idea pipeline — stored as task_findings with type='content_idea'. */
export interface CampaignIdea {
  id: string;
  brand_id: string;
  topic: string;
  angle: string;
  source: IdeaSource;
  score?: IdeaScore;
  consumed_at?: string | null;
  surface_fit?: Surface[];
  provenance?: CampaignIdeaProvenance | null;
}

export interface BriefReviewAction {
  id: string;
  brief_id: string;
  campaign_id: string;
  brand_id: string;
  action: ReviewAuditAction;
  actor: string;
  metadata: Record<string, unknown> | null;
  created_at: string;
}

export interface ReviewMetrics {
  rewrites_requested: number;
  approvals_opened: number;
  published_submissions: number;
  published_without_rewrite: number;
}

export interface ReviewRecentRun {
  id: string;
  task_slug: string;
  status: TaskRunStatus;
  findings_count: number;
  started_at: string;
  completed_at: string | null;
  brief_id: string | null;
  /** Same bounded job identity used by Activity and Job result. */
  presentation?: TaskRunPresentation;
}

export type ReviewTrustOperatorAction = "resolve_upstream" | "dismiss" | "ignore";

export interface ReviewTrustAlert {
  id: string;
  kind: TrustIssueKind | "run";
  severity?: OperatorWorkSeverity;
  code?: string | null;
  title: string;
  detail: string;
  explanation?: string | null;
  operator_action?: ReviewTrustOperatorAction | null;
  run_id: string | null;
  finding_id: string | null;
}

export interface ReviewApprovalItem extends Brief {
  content_packet?: ContentPacket | null;
  grounding?: OutputGroundingSummary | null;
  draft_document_key?: string | null;
  blog_revision_run?: {
    run_id: string;
    status: TaskRunStatus;
    current_node_id: string | null;
    error: string | null;
    feedback: string | null;
  } | null;
}

export interface OutputEvidenceItem {
  raw_datum_id: string;
  relation: string;
  label: string | null;
  url: string | null;
  extract: string | null;
  partial: boolean;
}

export interface OutputEvidenceResponse {
  output_id: string;
  revision_id: string | null;
  grounding: OutputGroundingSummary;
  evidence: OutputEvidenceItem[];
}

export interface OutputEditRequest {
  output_document: OutputDocument;
}

export interface OutputEditResponse {
  output_id: string;
  revision_id: string;
  output_document: OutputDocument;
  grounding: OutputGroundingSummary;
}

/** Who approved a brief and when (latest in_review → approved transition). Approval never publishes. */
export interface BriefApproval {
  brief_id: string;
  state: BriefState;
  approved_at: string;
  /** Null when the approver is not a known user (legacy events). */
  approved_by: { id: string; name: string | null; email: string | null } | null;
}

/** A brief past approval (approved, scheduled or published), for Review → Approved. */
export interface ApprovedBriefItem {
  id: string;
  campaign_id: string;
  campaign_name: string | null;
  title: string;
  surface: string | null;
  state: BriefState;
  published_url: string | null;
  published_at: string | null;
  /** Null for a brief whose approval predates the event log. */
  approved_at: string | null;
  approved_by: BriefApproval["approved_by"];
}

export interface ReviewDashboardData {
  week_briefs: Brief[];
  stuck_briefs: Brief[];
  awaiting_approval: ReviewApprovalItem[];
  todays_scheduled: Brief[];
  yesterdays_signal: TaskFinding[];
  recent_findings: TaskFinding[];
  recent_runs: ReviewRecentRun[];
  trust_alerts: ReviewTrustAlert[];
  pending_corrections_count: number;
  score_trend: BrandAuditScore[];
  review_metrics: ReviewMetrics;
}

export * from "./output-lineage";

export type CampaignPlanScheduleStatus = "active" | "paused";
/** Public campaign Schedule data. Claim and request-replay secrets never cross the API. */
export interface CampaignPlanSchedule {
  id: string;
  plan_id: string;
  campaign_id: string;
  brand_id: string;
  revision: number;
  status: CampaignPlanScheduleStatus;
  task_slug: string;
  definition_version: number;
  plan_revision: number;
  plan_title: string;
  objective: string;
  params: Record<string, unknown>;
  cron_expression: string;
  timezone: string;
  starts_at: string | null;
  ends_at: string;
  next_fire_at: string | null;
  last_fired_at: string | null;
  last_error: string | null;
  created_at: string;
  updated_at: string;
}
export interface CampaignPlanScheduleReadiness {
  brand_id: string;
  campaign_id: string;
  plan_id: string;
  plan_revision: number;
  definition_version: number;
  can_schedule: boolean;
  blockers: Array<{ code: string; message: string }>;
  /** Present on a Schedule manage view when its frozen inputs predate the live plan. */
  schedule_plan_revision?: number;
  schedule_definition_version?: number;
}
